import { FC, useEffect, useReducer, useRef, useState } from 'react';

import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import noop, { NoOp } from 'utils/noop';
import { getFileNameFromUrl } from 'utils/stringHelpers';

import { PanelContext, PanelContextState } from './Panel';
import { useTranslations } from '../hooks/useTranslations';
import {
  ImageAttrBatch,
  ImageAttrs,
  LinkAttrBatch,
  LinkAttrs,
  PanelView,
  imageAttrStateDefault,
  linkAttrStateDefault,
} from '../constants';
import { ImageAttrState, ImageMetadata, LinkAttrState, MovableInkBlock } from '../types';
import { getImageData, imageAttrReducer, linkAttrReducer } from '../utils';
import { useMovableInkBlockContext } from './MovableInkBlockContext';

export type Props = {
  canStyle?: boolean;
  onCancel?: NoOp;
  onClose?: NoOp;
  onInsert?: (imgAttrs: Record<string, string>, linkAttrs: Record<string, string>) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void;
  selectedEditorImageNode?: HTMLImageElement;
  selectedEditorImageAnchorNode?: HTMLAnchorElement;
};

export const PanelContextProvider: FC<React.PropsWithChildren<Props>> = ({
  canStyle = true,
  children,
  onCancel = noop,
  onClose = noop,
  onInsert,
  onSelect,
  selectedEditorImageNode,
  selectedEditorImageAnchorNode,
}) => {
  const { t } = useTranslations();
  const [debouncedPanelSearch, panelSearch, setPanelSearch] = useDebouncedState('', 500);
  const { setSelectedBlock } = useMovableInkBlockContext();
  const [isCampaignSelectorsSticky, setIsCampaignSelectorsSticky] = useState<boolean>(false);

  const [panelView, setPanelView] = useState<PanelView>(
    !!selectedEditorImageNode ? PanelView.Detail : PanelView.List,
  );
  const panelViewLive = useRef<PanelView>(
    !!selectedEditorImageNode ? PanelView.Detail : PanelView.List,
  );
  const [scrollBookmark, setScrollBookmark] = useState<number>(0);
  const [imageAttrState, setImageAttrState] = useReducer(imageAttrReducer, imageAttrStateDefault);
  const [imgMetadata, setImgMetadata] = useState<ImageMetadata>({
    height: 0,
    name: t('label.unnamedImage'),
    naturalAspectRatio: 1,
    width: 0,
  });
  const [linkAttrState, setLinkAttrState] = useReducer(linkAttrReducer, linkAttrStateDefault);
  const [nonCustomizableAttrs, setNonCustomizableAttrs] = useState<Record<string, string>>({});

  const handleSetPanelView = (pv: PanelView) => {
    setPanelView(pv);
    panelViewLive.current = pv;
  };

  const updateImageAttribute = async (block: MovableInkBlock) => {
    const { naturalHeight, naturalWidth } = await getImageData(block.imageUrl);
    const metadata: ImageMetadata = {
      campaignId: block?.campaignId, // MI campaign ID
      created: block.created,
      height: naturalHeight || 0,
      name: block.name || t('label.unnamedImage'),
      naturalAspectRatio: naturalHeight && naturalWidth ? naturalWidth / naturalHeight : 1,
      width: naturalWidth || 0,
    };
    // image attributes (limited expected set from MI image tags in embed code)
    const reImgAlt = new RegExp(`<img [^>]*?${ImageAttrs.ALT_DESC}\s*=\s*"(.+?)"`, 'gi');
    const reImgBorder = new RegExp(`<img [^>]*?${ImageAttrs.BORDER}\s*=\s*"(.+?)"`, 'gi');
    const reImgStyle = new RegExp(`<img [^>]*?${ImageAttrs.STYLES}\s*=\s*"(.+?)"`, 'gi');
    const imgAttrValues = {} as ImageAttrState;
    imgAttrValues[ImageAttrs.ALT_DESC] = (reImgAlt.exec(block.embedCode) || [])[1] || '';
    imgAttrValues[ImageAttrs.BORDER] = (reImgBorder.exec(block.embedCode) || [])[1] || '';
    imgAttrValues[ImageAttrs.SOURCE] = block.imageUrl || '';
    imgAttrValues[ImageAttrs.STYLES] = (reImgStyle.exec(block.embedCode) || [])[1] || '';
    // link attributes (limited expected set from MI link tags in embed code)
    const reLinkStyle = new RegExp(`<a [^>]*?${LinkAttrs.STYLES}\s*=\s*"(.+?)"`, 'gi');
    const linkAttrValues = {} as LinkAttrState;
    linkAttrValues[LinkAttrs.STYLES] = (reLinkStyle.exec(block.embedCode) || [])[1] || '';
    linkAttrValues[LinkAttrs.HREF] = block.aHref || '';
    // set values
    setImageAttrState({ type: ImageAttrBatch.SET_NEW, value: imgAttrValues });
    setLinkAttrState({ type: LinkAttrBatch.SET_NEW, value: linkAttrValues });
    setImgMetadata(metadata);
    setSelectedBlock(block);
  };

  useEffect(() => {
    const imgAttrValues = {} as ImageAttrState;
    const linkAttrValues = {} as LinkAttrState;
    if (selectedEditorImageNode) {
      // metadata
      const metadata: ImageMetadata = {
        height: selectedEditorImageNode.naturalHeight,
        name:
          getFileNameFromUrl(selectedEditorImageNode.getAttribute('src') || '') ||
          t('label.unnamedImage'),
        naturalAspectRatio:
          selectedEditorImageNode.naturalWidth / selectedEditorImageNode.naturalHeight,
        width: selectedEditorImageNode.naturalWidth,
      };
      setImgMetadata(metadata);
      // image attributes (limited expected set from MI image tags in embed code)
      const nodeHeight = (selectedEditorImageNode.getAttribute('height') || '').trim();
      const nodeWidth = (selectedEditorImageNode.getAttribute('width') || '').trim();
      imgAttrValues[ImageAttrs.ALT_DESC] = (
        selectedEditorImageNode.getAttribute('alt') || ''
      ).trim();
      imgAttrValues[ImageAttrs.BORDER] = (
        selectedEditorImageNode.getAttribute('border') || ''
      ).trim();
      imgAttrValues[ImageAttrs.HEIGHT] = parseInt(nodeHeight, 10)
        ? parseInt(nodeHeight, 10).toString()
        : ''; // correct nonInteger attribute values
      imgAttrValues[ImageAttrs.SOURCE] = (selectedEditorImageNode.getAttribute('src') || '').trim();
      imgAttrValues[ImageAttrs.STYLES] = (
        selectedEditorImageNode.getAttribute('style') || ''
      ).trim();
      imgAttrValues[ImageAttrs.WIDTH] = parseInt(nodeWidth, 10)
        ? parseInt(nodeWidth, 10).toString()
        : ''; // correct nonInteger attribute values
      // link attributes (limited expected set from MI link tags in embed code)
      linkAttrValues[LinkAttrs.STYLES] = (
        selectedEditorImageAnchorNode?.getAttribute('style') || ''
      ).trim();
      linkAttrValues[LinkAttrs.HREF] = (
        selectedEditorImageAnchorNode?.getAttribute('href') || ''
      ).trim();
      // set the attributes initial values based on the selected image element in the WYSIWYG editor
      setImageAttrState({ type: ImageAttrBatch.SET_NEW, value: imgAttrValues });
      setLinkAttrState({ type: LinkAttrBatch.SET_NEW, value: linkAttrValues });
    }
  }, []);

  const context: PanelContextState = {
    canStyle,
    debouncedPanelSearch,
    imageAttrState,
    imgMetadata,
    linkAttrState,
    nonCustomizableAttrs,
    onCancel,
    onClose,
    onInsert,
    onSelect,
    panelSearch,
    panelView,
    scrollBookmark,
    selectedEditorImageNode,
    setImageAttrState,
    setLinkAttrState,
    setNonCustomizableAttrs,
    setPanelSearch,
    setPanelView: handleSetPanelView,
    setScrollBookmark,
    updateImageAttribute,
    setIsCampaignSelectorsSticky,
    isCampaignSelectorsSticky,
  };

  return <PanelContext.Provider value={context}>{children}</PanelContext.Provider>;
};
